import {
  Badge,
  Button,
  Card,
  DataTable,
  EmptyState,
  Icon,
  Text,
  useToast,
  type DataTableColumn,
} from '@ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { ConfirmDialog } from '../../../app/ConfirmDialog';
import { errorMessage } from '../../../lib/errors';
import { formatDate } from '../../../lib/format';
import { useColumnSort, useSortMenu } from '../../../lib/useColumnSort';
import { useSession } from '../../identity/session';
import { useDeleteTreatment, useTreatments, type Treatment } from '../api';
import { EndTreatmentDialog } from '../components/EndTreatmentDialog';
import { isFiltered, RecordToolbar, useRecordFilter } from '../components/RecordFilters';
import { PageActions } from '../../../app/PageActions';
import { useRowMenu } from '../../../app/rowActions';
import { TreatmentDialog } from '../components/TreatmentDialog';
import { daysSince, formatAmount } from '../format';
import styles from './RecordPages.module.css';

/** All treatments, filterable (TRT-004). */
export function TreatmentsPage() {
  const { t } = useTranslation();
  const toast = useToast();
  const { can } = useSession();
  const rowMenu = useRowMenu();
  const [filter, setFilter, resetFilter] = useRecordFilter();
  const columnSort = useColumnSort();
  const [recording, setRecording] = useState(false);
  const [editing, setEditing] = useState<Treatment | null>(null);
  const [ending, setEnding] = useState<Treatment | null>(null);
  const [deleting, setDeleting] = useState<Treatment | null>(null);
  // Paged list: the server sorts, so "Weitere laden" continues in order (#188).
  const list = useTreatments({
    ...filter,
    ...(columnSort.value ? { sort: columnSort.value } : {}),
  });
  const remove = useDeleteTreatment();
  const rows = list.data?.pages.flatMap((p) => p.items) ?? [];
  const write = can('treatments.write');

  const columns: DataTableColumn<Treatment>[] = [
    {
      key: 'date',
      header: t('apiary.treatments.columns.date'),
      cell: (r) => formatDate(r.started_on),
      sortValue: (r) => r.started_on,
      width: '8rem',
    },
    {
      key: 'method',
      header: t('apiary.treatments.columns.method'),
      primary: true,
      cell: (r) => (
        <span>
          {r.method_name}
          {r.long_term && !r.ended_on && (
            <>
              {' '}
              <Badge tone="warning" size="sm" dot>
                {t('apiary.colonies.running', { count: daysSince(r.started_on) })}
              </Badge>
            </>
          )}
          {r.ended_on && (
            <span className={styles.sub}>
              {t('apiary.timeline.endedOn', { date: formatDate(r.ended_on) })}
            </span>
          )}
        </span>
      ),
      sortValue: (r) => r.method_name,
    },
    {
      key: 'colony',
      header: t('apiary.treatments.columns.colony'),
      cell: (r) => <Link to={`/voelker/${r.colony.id}`}>{r.colony.name}</Link>,
      sortValue: (r) => r.colony.name,
    },
    {
      key: 'location',
      header: t('apiary.treatments.columns.location'),
      cell: (r) => r.location?.name ?? '–',
      sortValue: (r) => r.location?.name,
    },
    {
      key: 'amount',
      header: t('apiary.treatments.columns.amount'),
      align: 'end',
      cell: (r) => (r.amount != null ? `${formatAmount(r.amount, 3)} ${r.unit ?? ''}` : '–'),
      sortValue: (r) => r.amount,
    },
    {
      key: 'by',
      header: t('apiary.treatments.columns.by'),
      cell: (r) => r.performed_by ?? '–',
      sortValue: (r) => r.performed_by,
    },
  ];

  const sortMenu = useSortMenu(columns, rows, columnSort);

  return (
    <div className={styles.page}>
      {write && (
        <PageActions>
          <Button iconStart={<Icon name="plus" size="sm" />} onClick={() => setRecording(true)}>
            {t('apiary.treatments.record')}
          </Button>
        </PageActions>
      )}
      <RecordToolbar
        value={filter}
        onChange={setFilter}
        onReset={resetFilter}
        sort={sortMenu}
        running
      />
      <Card padding="none" className={styles.card}>
        {list.error && (
          <Text tone="danger" role="alert" style={{ padding: 'var(--space-5)' }}>
            {errorMessage(list.error)}
          </Text>
        )}
        <DataTable
          caption={t('apiary.treatments.caption')}
          rows={rows}
          rowKey={(r) => r.id}
          {...columnSort}
          presorted
          loading={list.isPending}
          columns={columns}
          {...(write
            ? {
                rowActions: rowMenu.table<Treatment>(
                  (r) => `${r.method_name}, ${r.colony.name}`,
                  (r) => [
                    rowMenu.edit(() => setEditing(r)),
                    r.long_term &&
                      !r.ended_on && {
                        key: 'end',
                        label: t('apiary.treatment.end'),
                        onSelect: () => setEnding(r),
                      },
                    rowMenu.remove(() => setDeleting(r)),
                  ],
                ),
              }
            : {})}
          empty={
            isFiltered(filter) ? (
              <EmptyState
                title={t('common.noResults')}
                actions={
                  <Button variant="secondary" onClick={resetFilter}>
                    {t('common.resetFilters')}
                  </Button>
                }
              />
            ) : (
              <EmptyState icon="syringe" title={t('apiary.treatments.emptyTitle')} />
            )
          }
        />
        {list.hasNextPage && (
          <div className={styles.more}>
            <Button
              variant="secondary"
              loading={list.isFetchingNextPage}
              onClick={() => void list.fetchNextPage()}
            >
              {t('apiary.loadMore')}
            </Button>
          </div>
        )}
      </Card>
      {recording && <TreatmentDialog open onClose={() => setRecording(false)} />}
      {editing && <TreatmentDialog open treatment={editing} onClose={() => setEditing(null)} />}
      {ending && <EndTreatmentDialog open treatment={ending} onClose={() => setEnding(null)} />}
      <ConfirmDialog
        open={deleting !== null}
        onClose={() => {
          remove.reset();
          setDeleting(null);
        }}
        title={t('apiary.treatment.deleteTitle')}
        description={
          deleting
            ? `${deleting.method_name} · ${deleting.colony.name} · ${formatDate(deleting.started_on)}. ${t('apiary.treatment.deleteText')}`
            : undefined
        }
        confirmLabel={t('common.delete')}
        pending={remove.isPending}
        error={remove.error ? errorMessage(remove.error) : undefined}
        onConfirm={() =>
          deleting &&
          remove.mutate(deleting.id, {
            onSuccess: () => {
              toast.show(t('apiary.treatment.deleted'));
              setDeleting(null);
            },
          })
        }
      />
    </div>
  );
}
